import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Me } from "../api/contracts";
import { useApi } from "../api/api-context";

// The resolved principal (/v1/me) — the source of truth for the RBAC shell. Held
// once via react-query so every surface reads the same effective grants.
export interface PrincipalState {
  me: Me | null;
  isLoading: boolean;
  error: unknown;
}

const PrincipalContext = createContext<PrincipalState | null>(null);

export function PrincipalProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/v1/me"),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return (
    <PrincipalContext.Provider value={{ me: data ?? null, isLoading, error }}>
      {children}
    </PrincipalContext.Provider>
  );
}

/** Test seam: provide a fixed principal without a query/network. */
export function PrincipalContextProvider({
  value,
  children,
}: {
  value: PrincipalState;
  children: ReactNode;
}) {
  return <PrincipalContext.Provider value={value}>{children}</PrincipalContext.Provider>;
}

export function usePrincipal(): PrincipalState {
  const ctx = useContext(PrincipalContext);
  if (!ctx) throw new Error("usePrincipal must be used within a PrincipalProvider");
  return ctx;
}
